import { Component, inject, OnInit } from '@angular/core';
import { ApiConfig } from '@kildenconfig/api-config';
import { DialogService, DialogVariants } from '@kildencore/services/dialog.service';
import { MapService } from '@kildencore/services/map.service';
import { UploadService } from '@kildencore/services/upload.service';
import { formatFileSize as formatSize } from '@kildencore/tools';
import { environment } from '../../../../environments/environment';
import { GeoJSON, GPX, KML, TopoJSON } from 'ol/format';
import { Style } from 'ol/style';
import Icon from 'ol/style/Icon';
import proj4 from 'proj4';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

const UPLOADED_ID = 'uploadedVectorLayer';
declare let JSZip: any;
declare let EPSGUser: any;
declare let EPSG4326: any;
declare let shpLoader: any;
declare let SHPParser: any;
declare let dbfLoader: any;
declare let DBFParser: any;

@Component({
  selector: 'kilden3-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.css'],
  imports: [FormsModule, MatButton, MatProgressSpinner],
})
export class UploadFileComponent implements OnInit {
  private readonly _dialogService = inject(DialogService);
  private readonly _mapService = inject(MapService);
  private readonly _uploadService = inject(UploadService);
  private readonly strictAccept = '.geojson,.gpx,.json,.kml,.zip';
  public fileAcceptAttr: string | null = this.strictAccept;

  public fileContent = 'Content';
  public fileNames = '';
  public progress = 0;
  public uploadErrorMessage = '';
  public userMessage = '';
  public validFile = false;

  private files: Array<File> = [];
  private readers: Array<any> = [];
  private uploadError = false;

  public openFileBrowser = () => document.getElementById('fileUpload')?.click();

  public chosen = (e: any) => {
    this.reset();
    const n = e.target.files.length;
    const allowed = this.strictAccept
      .split(',')
      .map(p => p.trim().toLowerCase())
      .filter(p => p.startsWith('.'))
      .map(p => p.slice(1));
    this.fileNames = '';
    for (let i = 0; i < n; i++) {
      const f = e.target.files[i];
      if (this.isValidFile(f, allowed)) {
        this.files.push(f);
        if (this.fileNames) this.fileNames += '; ';
        this.fileNames += `${f.name}, ${formatSize(f.size)}`;
      }
    }
  };

  // prettier-ignore
  private readFile = (f: File) => new Promise((resolve, reject) => {
    const format_ = f.name.split('.').pop();
    const format = format_ ? format_.toLowerCase() : '';
    // prettier-ignore
    const zip = (format === 'zip');
    const reader = new FileReader();
    this.readers.push(reader); // keep track to be able to abort them

    reader.onprogress = (p: any) => {
      if (p.lengthComputable) this.progress = (p.loaded / p.total) * 100;
    };
    reader.onerror = (e: any) => {
      reject(`Feil ${f.name}`);
    };
    reader.onload = (l: any) => {
      this.fileContent = l.target.result;
      resolve(this.displayOnMap(f, format, l.target.result));
    };

    if (!zip) reader.readAsText(f, 'UTF-8'); else reader.readAsArrayBuffer(f);
  });

  public uploadFilesToMap = () => {
    this.fileContent = '';
    const promises: Array<any> = [];
    this.files.forEach((f: File) => {
      this.progress = 0.1; // show abort button
      promises.push(this.readFile(f));
    });
    Promise.allSettled(promises)
      .then(results => {
        this.progress = 100;
        const uploadedLayers: Array<any> = [];
        results.forEach(r => {
          if (r.status === 'fulfilled') {
            const layer = r.value;
            if (layer) uploadedLayers.push(layer);
          }
        });
        if (uploadedLayers.length) {
          this._mapService.zoomToUploads(uploadedLayers);
        }
        if (!this.uploadError) {
          this._uploadService.closeDialog('After reading files');
        }
      })
      .catch(e => {
        this.uploadError = true;
        this.uploadErrorMessage = 'Kan ikke laste. Feil med en eller flere filer.';
      });
  };

  public cancel = () => {
    this.userMessage = 'Avbrytet';
    this.progress = 0;
    // Abort file reader processes
    this.readers.forEach((r: any) => {
      if (r.readyState == 1) r.abort();
    });
  };

  private reset = () => {
    this.validFile = false;
    this.uploadError = false;
    this.userMessage = '';
    this.uploadErrorMessage = '';
    this.files.length = 0;
    this.fileContent = '';
    this.readers.length = 0;
  };

  private isValidFile = (f: File, allowed: Array<string>) => {
    const format_ = f.name.split('.').pop();
    const format = format_ ? format_.toLowerCase() : '';
    if (allowed.indexOf(format) < 0) {
      this.userMessage = 'Ulovlig format';
      this.validFile = false;
      return false;
    }
    // prettier-ignore
    const maxSize = 1048576 * ((format === 'zip') ? 1 : 20);
    if (f.size > maxSize) {
      this.userMessage = `Må være mindre enn ${formatSize(maxSize)}`;
      this.validFile = false;
      return false;
    }

    this.validFile = true;
    return true;
  };

  /**
   * Extract RGBA color from feature's ExtendedData.featureStyle JSON
   * KML ExtendedData often contains: { image_: { color_: [r, g, b, a] } }
   */
  private _extractColorFromFeature = (feature: any): [number, number, number, number] | null => {
    try {
      const extData = feature.get('featureStyle');
      if (!extData) return null;
      const parsed = typeof extData === 'string' ? JSON.parse(extData) : extData;
      const color = parsed?.image_?.color_;
      if (Array.isArray(color) && color.length === 4) {
        return color as [number, number, number, number];
      }
    } catch (e) {
      // Ignore parse errors
    }
    return null;
  };

  /**
   * Extract RGBA from Icon color when available.
   */
  private _extractColorFromIcon = (icon: Icon): [number, number, number, number] | null => {
    const c: any = icon.getColor?.();
    if (!c) return null;
    if (Array.isArray(c) && c.length >= 3) {
      const a = typeof c[3] === 'number' ? c[3] : 1;
      return [Number(c[0]), Number(c[1]), Number(c[2]), a];
    }
    if (typeof c === 'string') {
      const hex = c.trim();
      const mHex = /^#([0-9a-f]{6})$/i.exec(hex);
      if (mHex) {
        const v = mHex[1];
        return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16), 1];
      }
      const mRgb = /^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\)$/i.exec(hex);
      if (mRgb) {
        return [Number(mRgb[1]), Number(mRgb[2]), Number(mRgb[3]), mRgb[4] !== undefined ? Number(mRgb[4]) : 1];
      }
    }
    return null;
  };

  /**
   * Converts RGBA tuple to 6-char HEX used by svg_color endpoint.
   */
  private _toHexColor = (color: [number, number, number, number]): string => {
    const [r, g, b] = color;
    const toHex = (v: number) =>
      Math.max(0, Math.min(255, Math.round(v)))
        .toString(16)
        .padStart(2, '0');
    return `${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  /**
   * Builds server-side colored SVG URL via svg_color endpoint.
   */
  private _buildColoredSvgUrl = (svgSrc: string, color: [number, number, number, number]): string => {
    const hex = this._toHexColor(color);
    const absoluteUrl = /^https?:\/\//i.test(svgSrc) ? svgSrc : `${ApiConfig.guiUrl}/${svgSrc.replace(/^\/+/, '')}`;
    // Keep url unencoded to match existing project usage in print serializer.
    return `${ApiConfig.svgUrl}?color=${hex}&url=${absoluteUrl}`;
  };

  /**
   * Clone icon settings and only replace src, preserving placement in 3D.
   */
  private _cloneIconWithSrc = (original: Icon, src: string): Icon => {
    const o: any = original as any;
    const size: any = o.getSize ? o.getSize() : undefined;
    const anchorRaw: any = o.getAnchor ? o.getAnchor() : [0.5, 0.5];
    const anchorXUnits: any = o.getAnchorXUnits ? o.getAnchorXUnits() : 'fraction';
    const anchorYUnits: any = o.getAnchorYUnits ? o.getAnchorYUnits() : 'fraction';
    const anchor: number[] = Array.isArray(anchorRaw) ? [Number(anchorRaw[0]), Number(anchorRaw[1])] : [0.5, 0.5];

    // OL Icon#getAnchor can return normalized pixel values. Re-normalize when units are fractions.
    if (size && size[0] > 0 && anchorXUnits === 'fraction' && anchor[0] > 1) {
      anchor[0] = anchor[0] / size[0];
    }
    if (size && size[1] > 0 && anchorYUnits === 'fraction' && anchor[1] > 1) {
      anchor[1] = anchor[1] / size[1];
    }

    const iconOptions: any = {
      src,
      crossOrigin: o.getCrossOrigin ? o.getCrossOrigin() : undefined,
      anchor,
      anchorXUnits,
      anchorYUnits,
      anchorOrigin: o.getAnchorOrigin ? o.getAnchorOrigin() : 'top-left',
      offset: o.getOffset ? o.getOffset() : [0, 0],
      offsetOrigin: o.getOffsetOrigin ? o.getOffsetOrigin() : 'top-left',
      size,
      scale: o.getScale ? o.getScale() : 1,
      rotation: o.getRotation ? o.getRotation() : 0,
      rotateWithView: o.getRotateWithView ? o.getRotateWithView() : false,
      opacity: o.getOpacity ? o.getOpacity() : 1,
      displacement: o.getDisplacement ? o.getDisplacement() : [0, 0],
    };
    return new Icon(iconOptions);
  };

  /**
   * Convert same-origin colored SVG URL to PNG data URL for robust Cesium rendering.
   */
  private _svgUrlToPngDataUrl = async (url: string, size?: number[]): Promise<string | null> => {
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return null;
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = blobUrl;

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Image load timeout')), 5000);
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Image load failed'));
        };
      });

      const w = size && size[0] ? Math.max(1, Math.round(size[0])) : Math.max(1, img.naturalWidth || 48);
      const h = size && size[1] ? Math.max(1, Math.round(size[1])) : Math.max(1, img.naturalHeight || 48);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(blobUrl);
        return null;
      }
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(blobUrl);
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  };

  /**
   * Wait until Icon image has loaded (or failed) so 3D sync can pick it up.
   */
  private _waitForIconReady = (icon: Icon, timeoutMs = 8000): Promise<void> => {
    return new Promise(resolve => {
      const anyIcon: any = icon as any;
      const start = Date.now();
      const tick = () => {
        const state = anyIcon.getImageState ? anyIcon.getImageState() : undefined;
        if (state === 2 /* LOADED */ || state === 3 /* ERROR */ || Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(tick, 50);
      };
      if (anyIcon.load) {
        anyIcon.load();
      }
      tick();
    });
  };

  private displayOnMap = async (f: File, format: string, fileContent: string) => {
    const gpx = new GPX();
    const is3dActive = this._mapService.$is3dActive();
    const preserveKmlStylesIn3d = environment.preserveKmlStylesIn3d === true;
    // In 3D, KML extracted styles (especially point + text/icon styles) can cause unstable ol-cesium syncing.
    const kml = new KML({ extractStyles: preserveKmlStylesIn3d || !is3dActive });
    const geojson = new GeoJSON();
    const topojson = new TopoJSON();
    const ext2ol: { [key: string]: any } = { geojson, gpx, kml, topojson };
    let uploadedLayer: any;
    if (fileContent) {
      if (format) {
        const ms = this._mapService;
        const fp = is3dActive ? ms.projOn3dInit : ms.getCode();
        try {
          let features: any[] = [];
          if (format === 'zip') {
            const shpGeojson = await shp2geojsonAsync(fileContent);
            if (shpGeojson) {
              features = geojson.readFeatures(shpGeojson, { featureProjection: fp });
            }
          } else {
            features = ext2ol[format].readFeatures(fileContent, {
              featureProjection: fp,
            });
          }

          if (!features?.length) {
            this.uploadError = true;
            this.uploadErrorMessage = 'Kan ikke laste. Finner ingen geometri i filen.';
            this.progress = 0;
            return undefined;
          }

          // In 3D + KML: colorize SVG icons from ExtendedData before rendering
          if (is3dActive && format === 'kml' && preserveKmlStylesIn3d) {
            const iconsToWait: Icon[] = [];

            const colorizeIconStyle = async (feature: any, s: Style): Promise<void> => {
              const img = s.getImage();
              if (!(img instanceof Icon)) return;

              const src = img.getSrc();
              if (!src || !/\.svg(?:\?|#|$)/i.test(src)) return; // Only SVG icons

              const color = this._extractColorFromIcon(img) || this._extractColorFromFeature(feature);
              if (!color) return; // No color info

              const coloredUrl = this._buildColoredSvgUrl(src, color);
              const size = (img.getSize ? img.getSize() : undefined) as any;
              const pngDataUrl = await this._svgUrlToPngDataUrl(coloredUrl, size);
              const finalSrc = pngDataUrl || coloredUrl;

              // Replace icon source while preserving original placement settings.
              const replacementIcon = this._cloneIconWithSrc(img, finalSrc);
              s.setImage(replacementIcon);
              iconsToWait.push(replacementIcon);
            };

            const iconReplaceTasks: Promise<void>[] = [];

            features.forEach((feature: any) => {
              const raw = feature.getStyle();
              if (!raw) return;
              const resolved = typeof raw === 'function' ? raw(feature, 1) : raw;
              const styles: Style[] = Array.isArray(resolved)
                ? (resolved as Style[])
                : resolved
                  ? [resolved as Style]
                  : [];
              if (typeof raw === 'function' && styles.length > 0) {
                // Freeze resolved style on feature so later renders keep rewritten SVG URL.
                feature.setStyle(styles);
              }
              styles.forEach(s => iconReplaceTasks.push(colorizeIconStyle(feature, s)));
            });

            await Promise.allSettled(iconReplaceTasks);
            await Promise.allSettled(iconsToWait.map(icon => this._waitForIconReady(icon)));
          }

          if (
            this._dialogService.isOpen(DialogVariants.AREA_REPORT) ||
            this._dialogService.isOpen(DialogVariants.FOREST_REPORT) ||
            this._dialogService.isOpen(DialogVariants.SOIL_REPORT)
          ) {
            // Upload as attachment for report
            uploadedLayer = this._uploadService.updateReportLayer(features, f.name);
          } else {
            // Upload into separate layers and add to ActiveLayers list
            uploadedLayer = this._uploadService.addCustomThemeLayer(features, f.name);
          }

          this.userMessage = 'Lastet opp på kartet';
        } catch (e: any) {
          this.uploadError = true;
          this.uploadErrorMessage = 'Kan ikke laste. Feil med en eller flere filer.';
          this.progress = 0;
        }
      }
    }
    return uploadedLayer;
  };

  private isIOS(): boolean {
    const ua = navigator.userAgent || '';
    const touch = (navigator as any).maxTouchPoints || 0;

    // Vanlige iOS-enheter
    if (/iPad|iPhone|iPod/i.test(ua)) {
      return true;
    }

    // iPadOS 13+ kan rapportere "Mac" i UA, men har touch
    if (/Macintosh/i.test(ua) && touch > 1) {
      return true;
    }

    return false;
  }

  ngOnInit() {
    if (this.isIOS()) {
      this.fileAcceptAttr = null; // ← riktig for iOS fallback
    }
  }
  preventDrop(e: any) {
    e.preventDefault();
    e.dataTransfer.effectAllowed = 'none';
    e.dataTransfer.dropEffect = 'none';
  }
}

async function shp2geojsonAsync(buffer: any) {
  let geojson;

  try {
    const zipInput = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const zip = await JSZip.loadAsync(zipInput);
    const shpFile = zip.file(/\.shp$/i)[0];
    const dbfFile = zip.file(/\.dbf$/i)[0];
    const prjFile = zip.file(/\.prj$/i)[0];

    if (!shpFile || !dbfFile) {
      return undefined;
    }

    const [shpData, dbfData, prjString] = await Promise.all([
      shpFile.async('arraybuffer'),
      dbfFile.async('arraybuffer'),
      prjFile ? prjFile.async('text') : Promise.resolve(undefined),
    ]);

    EPSGUser = proj4(EPSG4326);
    if (prjString) {
      proj4.defs('EPSGUSER', prjString);
      try {
        EPSGUser = proj4('EPSGUSER');
      } catch (e) {
        console.error('Unsuported Projection: ' + e);
      }
    }

    shpLoader(new SHPParser().parse(shpData, shpFile.name), () => {}, proj4);

    const dbfEncodings = ['utf-8', 'utf-16', 'windows-1252'];
    for (const enc of dbfEncodings) {
      try {
        const dbfString = new TextDecoder(enc as any).decode(dbfData);
        dbfLoader(
          new DBFParser().parse(dbfData, dbfFile.name, dbfString, enc),
          (result: any) => (geojson = result),
          proj4
        );
        if (geojson) break;
      } catch {
        // try next encoding
      }
    }

    return geojson;
  } catch (e) {
    console.error('shp2geojsonAsync failed', e);
    return undefined;
  }
}
